/**
 * @fileoverview Avatar Companion Phase 1 Type Definitions
 * @module shared/types/AvatarCompanionPhase1
 * @version 2.0.0
 * @author Telegamez Avatar System
 * 
 * @description
 * Enhanced TypeScript type definitions for Avatar Companion Phase 1 implementation.
 * Includes the new expertise, personality, context, and audit systems.
 * These types extend the base AvatarCompanion types and provide comprehensive
 * typing for the unified API configuration endpoint and admin interface.
 * 
 * @dependencies
 * - zod: Runtime schema validation
 * - ./AvatarCompanion: Base avatar companion types
 * - Database schema compatibility with packages/shared/db/src/schema.ts
 * 
 * @features
 * - Expertise system with specializations and knowledge depth
 * - Personality system with communication styles and traits
 * - Context system for demographic and accessibility attributes
 * - Audit system for admin operations tracking
 * - Unified API configuration types
 * - Zero-shot personality inference types
 */

import { z } from 'zod';
import { 
  AvatarCompanion, 
  AvatarCompanionAPI, 
  NewAvatarCompanion,
  AvatarCompanionCategory 
} from './AvatarCompanion';

// =============================================================================
// EXPERTISE SYSTEM TYPES
// =============================================================================

/**
 * Available primary categories for companion expertise
 */
export enum ExpertisePrimaryCategory {
  SPORTS = 'sports',
  TECHNOLOGY = 'technology', 
  GAMING = 'gaming',
  EDUCATION = 'education',
  HEALTH_FITNESS = 'health-fitness',
  BUSINESS_FINANCE = 'business-finance',
  ENTERTAINMENT_MEDIA = 'entertainment-media',
  NEWS_CURRENT_EVENTS = 'news-current-events',
  LIFESTYLE_WELLNESS = 'lifestyle-wellness',
  CREATIVE_ARTS = 'creative-arts',
  SCIENCE_RESEARCH = 'science-research',
  GENERAL_KNOWLEDGE = 'general-knowledge'
}

/**
 * Expertise levels indicating companion's knowledge depth
 */
export enum ExpertiseLevel {
  BEGINNER = 'beginner',
  INTERMEDIATE = 'intermediate', 
  EXPERT = 'expert',
  WORLD_CLASS = 'world-class'
}

/**
 * Knowledge depth indicators for expertise assessment
 */
export enum KnowledgeDepth {
  SURFACE = 'surface',
  MODERATE = 'moderate',
  DEEP = 'deep', 
  COMPREHENSIVE = 'comprehensive'
}

/**
 * Companion expertise schema for validation
 */
export const CompanionExpertiseSchema = z.object({
  id: z.string().uuid(),
  companionId: z.string().uuid(),
  primaryCategory: z.nativeEnum(ExpertisePrimaryCategory),
  specializations: z.array(z.string()),
  expertiseLevel: z.nativeEnum(ExpertiseLevel),
  knowledgeDepth: z.nativeEnum(KnowledgeDepth),
  yearsExperience: z.number().int().min(0).nullable(),
  certifications: z.record(z.any()).nullable(),
  createdAt: z.date(),
  updatedAt: z.date()
});

/**
 * New companion expertise schema for creation
 */
export const NewCompanionExpertiseSchema = CompanionExpertiseSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true
});

export type CompanionExpertise = z.infer<typeof CompanionExpertiseSchema>;
export type NewCompanionExpertise = z.infer<typeof NewCompanionExpertiseSchema>;

// =============================================================================
// PERSONALITY SYSTEM TYPES
// =============================================================================

/**
 * Communication styles for personality system
 */
export enum CommunicationStyle {
  CASUAL = 'casual',
  PROFESSIONAL = 'professional',
  ENTHUSIASTIC = 'enthusiastic',
  ANALYTICAL = 'analytical',
  CONVERSATIONAL = 'conversational',
  MENTORING = 'mentoring'
}

/**
 * Energy levels for personality traits
 */
export enum EnergyLevel {
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low'
}

/**
 * Formality levels for communication style
 */
export enum FormalityLevel {
  VERY_FORMAL = 'very-formal',
  PROFESSIONAL = 'professional',
  CASUAL = 'casual',
  VERY_CASUAL = 'very-casual'
}

/**
 * Humor usage patterns
 */
export enum HumorUsage {
  FREQUENT = 'frequent',
  OCCASIONAL = 'occasional',
  MINIMAL = 'minimal',
  SITUATIONAL = 'situational'
}

/**
 * Response style preferences
 */
export enum ResponseStyle {
  CONCISE = 'concise',
  DETAILED = 'detailed',
  ADAPTIVE = 'adaptive'
}

/**
 * Motivational approach styles
 */
export enum MotivationalApproach {
  ENCOURAGING = 'encouraging',
  CHALLENGING = 'challenging',
  GENTLE = 'gentle'
}

/**
 * Companion personality schema for validation
 */
export const CompanionPersonalitySchema = z.object({
  id: z.string().uuid(),
  companionId: z.string().uuid(),
  coreTraits: z.array(z.string()),
  communicationStyle: z.nativeEnum(CommunicationStyle),
  interactionPatterns: z.array(z.string()),
  energyLevel: z.nativeEnum(EnergyLevel),
  formalityLevel: z.nativeEnum(FormalityLevel),
  humorUsage: z.nativeEnum(HumorUsage),
  responseStyle: z.nativeEnum(ResponseStyle),
  motivationalApproach: z.nativeEnum(MotivationalApproach),
  createdAt: z.date(),
  updatedAt: z.date()
});

/**
 * New companion personality schema for creation
 */
export const NewCompanionPersonalitySchema = CompanionPersonalitySchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true
});

export type CompanionPersonality = z.infer<typeof CompanionPersonalitySchema>;
export type NewCompanionPersonality = z.infer<typeof NewCompanionPersonalitySchema>;

// =============================================================================
// CONTEXT SYSTEM TYPES
// =============================================================================

/**
 * Cultural perspectives for context adaptation
 */
export enum CulturalPerspective {
  WESTERN = 'western',
  GLOBAL = 'global',
  TECH_CULTURE = 'tech-culture',
  ACADEMIC = 'academic',
  BUSINESS = 'business',
  CREATIVE = 'creative'
}

/**
 * Target age groups for companion context
 */
export enum TargetAgeGroup {
  TEENS = 'teens',
  YOUNG_ADULTS = 'young-adults',
  ADULTS = 'adults',
  SENIORS = 'seniors'
}

/**
 * Learning style preferences
 */
export enum LearningStylePreference {
  VISUAL = 'visual',
  AUDITORY = 'auditory',
  KINESTHETIC = 'kinesthetic',
  READING_WRITING = 'reading-writing'
}

/**
 * Accessibility features
 */
export enum AccessibilityFeature {
  SIMPLE_LANGUAGE = 'simple-language',
  PATIENT_EXPLANATIONS = 'patient-explanations',
  CLEAR_STRUCTURE = 'clear-structure',
  REPETITION_FRIENDLY = 'repetition-friendly'
}

/**
 * Companion context schema for validation
 */
export const CompanionContextSchema = z.object({
  id: z.string().uuid(),
  companionId: z.string().uuid(),
  targetAgeGroups: z.array(z.nativeEnum(TargetAgeGroup)),
  culturalPerspective: z.nativeEnum(CulturalPerspective),
  learningStylePreference: z.array(z.nativeEnum(LearningStylePreference)),
  accessibilityFeatures: z.array(z.nativeEnum(AccessibilityFeature)),
  createdAt: z.date(),
  updatedAt: z.date()
});

/**
 * New companion context schema for creation
 */
export const NewCompanionContextSchema = CompanionContextSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true
});

export type CompanionContext = z.infer<typeof CompanionContextSchema>;
export type NewCompanionContext = z.infer<typeof NewCompanionContextSchema>;

// =============================================================================
// AUDIT SYSTEM TYPES
// =============================================================================

/**
 * Audit action types
 */
export enum AuditAction {
  CREATED = 'created',
  UPDATED = 'updated',
  DELETED = 'deleted',
  ACTIVATED = 'activated',
  DEACTIVATED = 'deactivated',
  IMPORTED = 'imported',
  EXPORTED = 'exported'
}

/**
 * Table names for audit tracking
 */
export enum AuditTableName {
  AVATAR_COMPANIONS = 'avatar_companions',
  COMPANION_EXPERTISE = 'companion_expertise',
  COMPANION_PERSONALITY = 'companion_personality',
  COMPANION_CONTEXT = 'companion_context'
}

/**
 * Companion audit log schema for validation
 */
export const CompanionAuditLogSchema = z.object({
  id: z.string().uuid(),
  companionId: z.string().uuid().nullable(),
  action: z.nativeEnum(AuditAction),
  tableName: z.nativeEnum(AuditTableName),
  oldValues: z.record(z.any()).nullable(),
  newValues: z.record(z.any()).nullable(),
  changedFields: z.array(z.string()).nullable(),
  userEmail: z.string().email().refine(email => email.endsWith('@telegamez.com')),
  userSessionId: z.string().nullable(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  reason: z.string().nullable(),
  createdAt: z.date()
});

/**
 * New companion audit log schema for creation
 */
export const NewCompanionAuditLogSchema = CompanionAuditLogSchema.omit({
  id: true,
  createdAt: true
});

export type CompanionAuditLog = z.infer<typeof CompanionAuditLogSchema>;
export type NewCompanionAuditLog = z.infer<typeof NewCompanionAuditLogSchema>;

// =============================================================================
// ENHANCED AVATAR COMPANION TYPES
// =============================================================================

/**
 * Enhanced avatar companion status values for Phase 1
 */
export enum AvatarCompanionStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive', 
  DRAFT = 'draft',
  DEPRECATED = 'deprecated',
  UNDER_REVIEW = 'under-review'
}

/**
 * Complete avatar companion profile with all Phase 1 enhancements
 */
export interface CompleteCompanionProfile extends AvatarCompanion {
  // Phase 1 enhancements to base avatar
  adminNotes: string | null;
  qualityScore: number;
  lastReviewedAt: Date | null;
  lastReviewedBy: string | null;
  version: number;
  status: AvatarCompanionStatus;
  
  // Related data
  expertise: CompanionExpertise[];
  personality: CompanionPersonality[];
  context: CompanionContext[];
  auditLogs?: CompanionAuditLog[];
}

/**
 * API-serialized version of complete companion profile
 */
export interface CompleteCompanionProfileAPI extends AvatarCompanionAPI {
  // Phase 1 enhancements (serialized)
  adminNotes: string | null;
  qualityScore: number;
  lastReviewedAt: string | null;
  lastReviewedBy: string | null;
  version: number;
  status: string;
  
  // Related data (serialized)
  expertise: Array<{
    id: string;
    primaryCategory: string;
    specializations: string[];
    expertiseLevel: string;
    knowledgeDepth: string;
    yearsExperience: number | null;
    certifications: Record<string, any> | null;
  }>;
  personality: Array<{
    id: string;
    coreTraits: string[];
    communicationStyle: string;
    interactionPatterns: string[];
    energyLevel: string;
    formalityLevel: string;
    humorUsage: string;
    responseStyle: string;
    motivationalApproach: string;
  }>;
  context: Array<{
    id: string;
    targetAgeGroups: string[];
    culturalPerspective: string;
    learningStylePreference: string[];
    accessibilityFeatures: string[];
  }>;
}

// =============================================================================
// UNIFIED API CONFIGURATION TYPES
// =============================================================================

/**
 * Dynamic prompt configuration generated from companion profile
 */
export interface DynamicPromptConfig {
  /** Base personality prompt incorporating all traits */
  basePersonality: string;
  
  /** Expertise-specific knowledge prompts */
  expertisePrompts: {
    [category: string]: string;
  };
  
  /** Context-aware adaptations */
  contextAdaptations: {
    ageGroup?: string;
    culturalPerspective?: string;
    learningStyle?: string;
    accessibility?: string[];
  };
  
  /** Communication style directives */
  communicationStyle: {
    formality: string;
    energy: string;
    humor: string;
    responseStyle: string;
    motivationalApproach: string;
  };
}

/**
 * Enhanced unified API configuration response for Phase 1
 */
export interface UnifiedApiConfigResponse {
  /** Configuration metadata */
  config: {
    version: string;
    generatedAt: string;
    companionId: string;
    roomId: string;
  };
  
  /** Dynamic prompts generated from companion profile */
  prompts: {
    agents: {
      conversational_agent: {
        prompt: string;
        expertise_context: string;
        personality_traits: string[];
        communication_directives: string;
      };
      room_context_agent?: {
        prompt: string;
        context_awareness: string;
      };
    };
  };
  
  /** Provider configurations */
  providers: {
    voice: {
      provider: 'cartesia';
      voice_id: string;
      settings?: Record<string, any>;
    };
    llm: {
      // Keystone: 'groq' retired 2026-04-23. 'google' disabled pending GeminiResponsesAdapter.
      provider: 'openai' | 'xai';
      model: string;
      settings?: Record<string, any>;
    };
    stt?: {
      provider: string;
      settings?: Record<string, any>;
    };
  };
  
  /** Companion profile summary */
  companion: {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    category: string;
    modelPath: string;
    videoPreviewUrl: string | null;
    status: string;
    version: number;
    qualityScore: number;
  };
  
  /** Caching metadata */
  cache: {
    ttl: number;
    key: string;
    generated_at: string;
  };
}

// =============================================================================
// ZERO-SHOT PERSONALITY INFERENCE TYPES
// =============================================================================

/**
 * OCEAN personality model values for zero-shot inference
 */
export interface OceanPersonalityValues {
  /** Openness to experience (0-1) */
  openness: number;
  
  /** Conscientiousness (0-1) */
  conscientiousness: number;
  
  /** Extraversion (0-1) */
  extraversion: number;
  
  /** Agreeableness (0-1) */
  agreeableness: number;
  
  /** Neuroticism (0-1) */
  neuroticism: number;
}

/**
 * Speaker personality state for caching
 */
export interface SpeakerPersonalityState {
  /** Speaker identifier */
  speakerId: string;
  
  /** Inferred OCEAN values */
  oceanValues: OceanPersonalityValues;
  
  /** Confidence score (0-1) */
  confidence: number;
  
  /** Communication patterns observed */
  observedPatterns: string[];
  
  /** Timestamp of inference */
  inferredAt: Date;
  
  /** Cache expiration */
  expiresAt: Date;
}

/**
 * Personality inference request
 */
export interface PersonalityInferenceRequest {
  /** Speaker to analyze */
  speakerId: string;
  
  /** Recent conversation history */
  conversationHistory: Array<{
    content: string;
    timestamp: Date;
    speaker: string;
  }>;
  
  /** Optional context about the speaker */
  context?: {
    demographics?: Record<string, any>;
    preferences?: Record<string, any>;
  };
}

/**
 * Personality inference response
 */
export interface PersonalityInferenceResponse {
  /** Success status */
  success: boolean;
  
  /** Inferred personality state */
  personalityState?: SpeakerPersonalityState;
  
  /** Generated personality overlay */
  personalityOverlay?: {
    coreTraits: string[];
    communicationStyle: string;
    interactionPatterns: string[];
    energyLevel: string;
    formalityLevel: string;
    humorUsage: string;
    responseStyle: string;
    motivationalApproach: string;
  };
  
  /** Error information if failed */
  error?: {
    message: string;
    code: string;
  };
  
  /** Cache metadata */
  cache: {
    hit: boolean;
    ttl: number;
    key: string;
  };
}

// =============================================================================
// ADMIN INTERFACE TYPES
// =============================================================================

/**
 * Admin companion creation request
 */
export interface AdminCompanionCreateRequest {
  /** Base companion data */
  companion: Omit<NewAvatarCompanion, 'isActive'> & {
    status: AvatarCompanionStatus;
    adminNotes?: string;
    qualityScore?: number;
  };
  
  /** Expertise configuration */
  expertise: Omit<NewCompanionExpertise, 'companionId'>[];
  
  /** Personality configuration */
  personality: Omit<NewCompanionPersonality, 'companionId'>[];
  
  /** Context configuration */
  context: Omit<NewCompanionContext, 'companionId'>[];
  
  /** Admin metadata */
  admin: {
    userEmail: string;
    reason?: string;
    userSessionId?: string;
  };
}

/**
 * Admin companion update request
 */
export interface AdminCompanionUpdateRequest {
  /** Updated companion data */
  companion?: Partial<{
    name: string;
    slug: string;
    description: string | null;
    modelPath: string;
    voiceId: string;
    videoPreviewUrl: string | null;
    promptKeywords: string[] | null;
    category: string;
    tags: string[] | null;
    isActive: boolean;
    status: AvatarCompanionStatus;
    adminNotes: string | null;
    qualityScore: number;
    // Custom system prompt override (bypasses template generation if set)
    customPrompt?: string | null;
    // Companion Brain configuration (per-companion LLM)
    brainConfig?: Record<string, unknown> | null;
    // Admin-curated whitelist flag: whether hosts may opt this companion into adult mode
    adultCapable?: boolean;
  }>;
  
  /** Updated expertise data */
  expertise?: Array<{
    id?: string; // If provided, update existing; if not, create new
    primaryCategory: string;
    specializations: string[];
    expertiseLevel: string;
    knowledgeDepth: string;
    yearsExperience?: number;
    certifications?: Record<string, any>;
  }>;
  
  /** Updated personality data */
  personality?: Array<{
    id?: string; // If provided, update existing; if not, create new
    coreTraits: string[];
    communicationStyle: string;
    interactionPatterns: string[];
    energyLevel: string;
    formalityLevel: string;
    humorUsage: string;
    responseStyle: string;
    motivationalApproach: string;
  }>;
  
  /** Updated context data */
  context?: Array<{
    id?: string; // If provided, update existing; if not, create new
    targetAgeGroups: string[];
    culturalPerspective: string;
    learningStylePreference: string[];
    accessibilityFeatures: string[];
  }>;
  
  /** Admin metadata */
  admin: {
    userEmail: string;
    reason?: string;
    userSessionId?: string;
  };
}

/**
 * Admin companion response
 */
export interface AdminCompanionResponse {
  success: boolean;
  data?: CompleteCompanionProfileAPI;
  error?: {
    message: string;
    code: string;
    field?: string;
  };
  audit?: {
    logId: string;
    action: string;
    timestamp: string;
  };
}

// =============================================================================
// PERFORMANCE AND CACHING TYPES
// =============================================================================

/**
 * Cache key patterns for Avatar Companion Phase 1
 */
export enum CacheKeyPattern {
  AVATAR_PROFILE = 'avatar',
  SEARCH_RESULTS = 'search',
  CONFIG_UNIFIED = 'config',
  PERSONALITY_STATE = 'pstate',
  EXPERTISE_CATEGORIES = 'expertise:categories',
  PERSONALITY_TRAITS = 'personality:traits'
}

/**
 * Cache invalidation strategy for companion updates
 */
export interface CacheInvalidationStrategy {
  /** Patterns to invalidate */
  patterns: string[];
  
  /** Invalidation method */
  method: 'immediate_purge' | 'versioned_cache_bust' | 'selective_pattern_purge' | 'user_scoped_clear';
  
  /** Batch settings */
  batch?: {
    enabled: boolean;
    size: number;
    delay: number;
  };
}

// =============================================================================
// EXPORTS
// =============================================================================

export * from './AvatarCompanion'; // Re-export base types for convenience